/**
 * StartupDetectionBanner — Informational banner shown when a previously-seen startup
 * is detected while staging a deck. Surfaces the match but NEVER triggers analysis.
 * Resolution of which startup_id to use happens when the CEO clicks "Analyze Deal".
 *
 * Authority: DS_INPUT_TRIGGER_REDESIGN_2026-06-02 §2 (detection de-coupled from triggering)
 * Config: DS_COLORS, DS_TYPOGRAPHY, ACTION_BUTTONS from ui-invariants.js
 *         AGENT_CONFIG.deal_evaluator.callDecisionLabels, INTENT_TO_BUTTON from task-display-config.js
 *
 * WI-DS-DETECT-002 T2 — updated: banner is now informational only.
 */

import React from 'react';
import {
  DEAL_SCREEN_CARD,
  DS_COLORS,
  DS_TYPOGRAPHY,
  ACTION_BUTTONS,
} from '@/config/ui-invariants';
import { AGENT_CONFIG, INTENT_TO_BUTTON } from '@/config/task-display-config';

const CALL_DECISION_LABELS = AGENT_CONFIG.deal_evaluator?.callDecisionLabels || {};

function formatDate(dateStr) {
  if (!dateStr) return '';
  try {
    const d = new Date(dateStr);
    return d.toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric' });
  } catch {
    return dateStr;
  }
}

/**
 * Props:
 *   detectionResult: { state, extracted_name, match: { startup_id, name, last_decision, last_date }, confidence }
 *   onViewPrevious: () => void  — navigate to the existing startup record (no pipeline start)
 *   onNotSameStartup: () => void  — dismiss the match and treat as a new startup (possible_match only)
 *   loading: boolean
 *
 * Removed props (no longer accepted):
 *   onRunFresh — pipeline starts only via the "Analyze Deal" button, not from this banner.
 */
export default function StartupDetectionBanner({
  detectionResult,
  onViewPrevious,
  onNotSameStartup,
  loading,
}) {
  if (!detectionResult || detectionResult.state === 'new_startup' || !detectionResult.match) return null;

  const { state, match } = detectionResult;
  const isPossible = state === 'possible_match';

  const title = state === 'match_found'
    ? `Looks like an existing startup: ${match.name}`
    : `Possible match — ${match.name}?`;

  const decisionLabel = CALL_DECISION_LABELS[match.last_decision] || match.last_decision || '';
  const dateFormatted = formatDate(match.last_date);
  const subLine = [
    dateFormatted && `Last analysed: ${dateFormatted}`,
    decisionLabel,
    'Click Analyze Deal to run a fresh analysis, or view existing below.',
  ]
    .filter(Boolean)
    .join(' · ');

  const neutralBtn = INTENT_TO_BUTTON.neutral;
  const mutedBtn = INTENT_TO_BUTTON.muted;

  // # DECISION: Using inline styles consistent with all other Deal Screen components
  // (DealScreenDetailView, DealScreenInboxCard) rather than Tailwind classes, because the
  // entire DS component family uses style props from token objects. Mixing would break pattern.
  return (
    <div style={{
      background: DEAL_SCREEN_CARD.shell.background,
      border: `1px solid ${DEAL_SCREEN_CARD.shell.border}`,
      borderRadius: DEAL_SCREEN_CARD.shell.borderRadius,
      padding: `${DEAL_SCREEN_CARD.shell.paddingTop} ${DEAL_SCREEN_CARD.shell.paddingRight} ${DEAL_SCREEN_CARD.shell.paddingBottom} ${DEAL_SCREEN_CARD.shell.paddingLeft}`,
      display: 'flex',
      gap: '16px',
      alignItems: 'stretch',
    }}>
      {/* Left accent bar — navy */}
      <div style={{
        width: DEAL_SCREEN_CARD.accentBar.width,
        background: DEAL_SCREEN_CARD.accentBar.worthACall,
        borderRadius: '2px',
        flexShrink: 0,
      }} />

      <div style={{ flex: 1, display: 'flex', flexDirection: 'column', gap: '12px' }}>
        {/* Title — informational, no action implied */}
        <div>
          <h3 style={{
            ...DS_TYPOGRAPHY.cardTitle,
            color: DS_COLORS.text.primary,
            margin: 0,
          }}>
            {title}
          </h3>
          {subLine && (
            <p style={{
              ...DS_TYPOGRAPHY.bodySm,
              color: DS_COLORS.text.secondary,
              margin: '4px 0 0',
            }}>
              {subLine}
            </p>
          )}
        </div>

        {/* Action buttons — navigation and dismissal only, no pipeline trigger */}
        <div style={{ display: 'flex', gap: '10px', flexWrap: 'wrap' }}>
          {/* View previous existing analysis — neutral outline */}
          <button
            onClick={onViewPrevious}
            disabled={loading}
            className={`${neutralBtn.bg} ${neutralBtn.text} ${neutralBtn.border} ${neutralBtn.hover}`}
            style={{
              ...DS_TYPOGRAPHY.actionBtn,
              display: 'inline-flex',
              alignItems: 'center',
              minHeight: ACTION_BUTTONS.rerun.minHeight,
              padding: '10px 20px',
              borderWidth: '1.5px',
              borderStyle: 'solid',
              borderRadius: '8px',
              cursor: loading ? 'wait' : 'pointer',
              opacity: loading ? 0.6 : 1,
              transition: 'all 150ms',
            }}
          >
            View Existing Analysis
          </button>

          {/* Not the same startup — muted dismiss, only for possible_match */}
          {isPossible && (
            <button
              onClick={onNotSameStartup}
              disabled={loading}
              className={`${mutedBtn.bg} ${mutedBtn.text} ${mutedBtn.border} ${mutedBtn.hover}`}
              style={{
                ...DS_TYPOGRAPHY.actionBtn,
                display: 'inline-flex',
                alignItems: 'center',
                minHeight: ACTION_BUTTONS.rerun.minHeight,
                padding: '10px 20px',
                borderWidth: '1.5px',
                borderStyle: 'solid',
                borderRadius: '8px',
                cursor: loading ? 'wait' : 'pointer',
                opacity: loading ? 0.6 : 1,
                transition: 'all 150ms',
              }}
            >
              Not the same startup
            </button>
          )}
        </div>
      </div>
    </div>
  );
}
